// "Your Calorie Target" section — the story behind the number in the header.
//
// Two independent halves, each with its own gate:
//   • the step chart, shown when the closed-loop controller has run at least once
//   • the explainer, shown only at plan schema 2 (`loop` is non-null)
// A user on neither sees nothing at all — no empty card, no placeholder.
//
// The step line runs through APPLIED runs only. A run that recomputed the target
// but did not apply it never produced a number this user ate to, so drawing it as
// part of the line would invent history; those runs render as faint dots instead.
//
// What this component may show is deliberately bounded: the target, its floor,
// how many days were logged, and how many weigh-ins there were. The controller's
// internal energy-balance estimates are not in the API contract and must not be
// added here — the mechanism is explained in words, not in modelling intermediates.

import type { DashboardLoop, DashboardTargetPoint } from '../lib/types'
import type { Strings } from '../lib/i18n'

interface Props {
  history: DashboardTargetPoint[]
  loop: DashboardLoop | null
  strings: Strings
}

// Nominal controller lookback, used when the synced plan omits window_days.
const DEFAULT_WINDOW_DAYS = 28

const CHART_W = 300
const CHART_H = 120
const PAD_L = 36
const PAD_R = 36
const PAD_T = 12
const PAD_B = 24

const PLOT_RIGHT = CHART_W - PAD_R
const PLOT_BOTTOM = CHART_H - PAD_B

// Compact date label: YYYY-MM-DD → MM-DD (matches TrendChart).
function shortDate(d: string) {
  return d.slice(5)
}

// Single point sits centred rather than pinned to the left edge.
function xScale(i: number, n: number) {
  if (n <= 1) return PAD_L + (PLOT_RIGHT - PAD_L) / 2
  return PAD_L + (i / (n - 1)) * (PLOT_RIGHT - PAD_L)
}

function yScale(v: number, min: number, max: number) {
  const span = Math.max(max - min, 0.01)
  return PAD_T + ((max - v) / span) * (PLOT_BOTTOM - PAD_T)
}

// The target this run left in force. An applied run set a new one; a run that did
// not apply left its predecessor standing, so plotting its proposed number would
// draw a target the user was never given. Falls back to the run's own number when
// there is no predecessor (the very first run).
function inForceTarget(h: DashboardTargetPoint): number {
  if (h.applied) return h.target_kcal
  return h.prev_target_kcal ?? h.target_kcal
}

function TargetChart({
  history,
  floorKcal,
  floorLabel,
  ariaLabel,
}: {
  history: DashboardTargetPoint[]
  floorKcal: number | null
  floorLabel: string
  ariaLabel: string
}) {
  const n = history.length
  const values = history.map(inForceTarget)
  if (floorKcal != null) values.push(floorKcal)

  const rawMin = Math.min(...values)
  const rawMax = Math.max(...values)
  // A target that has never moved would otherwise collapse to a zero-height
  // domain and pin the line to the top of the plot; give it room to sit mid-card.
  const pad = rawMax - rawMin < 1 ? 50 : (rawMax - rawMin) * 0.15
  const min = rawMin - pad
  const max = rawMax + pad

  const pts = history.map((h, i) => ({
    x: xScale(i, n),
    y: yScale(inForceTarget(h), min, max),
    applied: h.applied,
  }))

  // Step-after: hold the target flat until the run that changed it, then jump.
  // The trailing segment to the right edge says the last target is still in force
  // (it is zero-length once there are two or more runs, and carries a single-run
  // history across the card).
  let stepPath = `M ${pts[0].x},${pts[0].y}`
  for (let i = 1; i < pts.length; i++) {
    stepPath += ` L ${pts[i].x},${pts[i - 1].y} L ${pts[i].x},${pts[i].y}`
  }
  stepPath += ` L ${PLOT_RIGHT},${pts[pts.length - 1].y}`

  const floorY = floorKcal == null ? null : yScale(floorKcal, min, max)
  // One label when the target has never moved, two otherwise.
  const yLabels = rawMax === rawMin ? [rawMax] : [rawMax, rawMin]

  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="trend-svg" role="img" aria-label={ariaLabel}>
      {floorY != null && (
        <>
          <line
            x1={PAD_L}
            y1={floorY}
            x2={PLOT_RIGHT}
            y2={floorY}
            stroke="var(--fg-subtle)"
            strokeWidth="1"
            strokeDasharray="4 3"
            opacity="0.7"
          />
          <text x={PLOT_RIGHT - 2} y={floorY - 3} fontSize="8" fill="var(--fg-muted)" textAnchor="end">
            {floorLabel} {Math.round(floorKcal as number)}
          </text>
        </>
      )}

      {stepPath && (
        <path
          d={stepPath}
          fill="none"
          stroke="var(--brand-primary)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}

      {/* A run that changed nothing sits ON the line as a quiet dot — "checked,
          left alone". The final run is the target in force today, so it carries
          the emphasis whether or not it was the one that moved it. */}
      {pts.map((p, i) => {
        const isLast = i === pts.length - 1
        if (isLast) return <circle key={`p${i}`} cx={p.x} cy={p.y} r="4" fill="var(--brand-primary)" />
        return p.applied ? (
          <circle key={`p${i}`} cx={p.x} cy={p.y} r="3" fill="var(--brand-primary)" opacity="0.6" />
        ) : (
          <circle key={`p${i}`} cx={p.x} cy={p.y} r="1.8" fill="var(--fg-subtle)" opacity="0.6" />
        )
      })}

      {yLabels.map((v) => (
        <text key={v} x={PAD_L - 4} y={yScale(v, min, max) + 4} fontSize="9" fill="var(--fg-muted)" textAnchor="end">
          {Math.round(v)}
        </text>
      ))}

      {n >= 2 && (
        <>
          <text x={xScale(0, n)} y={CHART_H - 4} fontSize="9" fill="var(--fg-muted)" textAnchor="middle">
            {shortDate(history[0].date)}
          </text>
          <text x={xScale(n - 1, n)} y={CHART_H - 4} fontSize="9" fill="var(--fg-muted)" textAnchor="middle">
            {shortDate(history[n - 1].date)}
          </text>
        </>
      )}
      {n === 1 && (
        <text x={xScale(0, n)} y={CHART_H - 4} fontSize="9" fill="var(--fg-muted)" textAnchor="middle">
          {shortDate(history[0].date)}
        </text>
      )}
    </svg>
  )
}

function TargetExplainer({ loop, strings: s }: { loop: DashboardLoop; strings: Strings }) {
  const windowDays = loop.window_days ?? DEFAULT_WINDOW_DAYS
  const phaseNote = loop.phase ? s.targetPhaseNote(loop.phase) : ''
  // updated_at is an ISO timestamp; fmtDate wants the calendar day.
  const updatedOn = /^\d{4}-\d{2}-\d{2}/.test(loop.updated_at ?? '')
    ? (loop.updated_at as string).slice(0, 10)
    : null

  const stats: string[] = []
  if (loop.coverage != null) {
    stats.push(s.targetCoverageStat(Math.round(Math.min(1, Math.max(0, loop.coverage)) * 100)))
  }
  if (loop.n_weighins != null) stats.push(s.targetWeighinsStat(loop.n_weighins))

  return (
    <div className="target-explainer">
      <div className="target-explainer-title">{s.targetExplainerTitle}</div>
      <p className="target-explainer-body">{s.targetExplainerBody(windowDays)}</p>
      {phaseNote && <p className="target-explainer-note">{phaseNote}</p>}

      {stats.length > 0 && (
        <>
          <div className="target-stats-label">{s.targetAccuracyLabel}</div>
          <div className="target-stats">
            {stats.map((t) => (
              <span key={t} className="target-stat">
                {t}
              </span>
            ))}
          </div>
        </>
      )}

      {loop.floor_kcal != null && (
        <p className="target-explainer-note">
          {s.targetFloorNote(Math.round(loop.floor_kcal))}
          {loop.floor_binding ? ` ${s.targetFloorBindingNote}` : ''}
        </p>
      )}

      {updatedOn && <div className="target-updated">{s.targetUpdatedLabel(s.fmtDate(updatedOn))}</div>}
    </div>
  )
}

export function TargetHistory({ history, loop, strings: s }: Props) {
  const rows = history ?? []
  if (rows.length === 0 && !loop) return null

  return (
    <div className="dash-card dash-target-card">
      <div className="dash-card-title">{s.targetHistoryTitle}</div>
      {rows.length > 0 && (
        <TargetChart
          history={rows}
          floorKcal={loop?.floor_kcal ?? null}
          floorLabel={s.targetFloorLabel}
          ariaLabel={s.targetChartAria}
        />
      )}
      {loop && <TargetExplainer loop={loop} strings={s} />}
    </div>
  )
}
